import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FamilyRegistrationStudent } from '../../shared-modules/family-registration/family-registration-student.model';
import { StudentDailyAttendance } from '../../student360/attendance/daily-attendance/student-daily-attendance.model';
import { OnlinePaymentConfiguration } from '../../student360/online-payment/models/online-payment-config.model';
import { Portfolio } from '../../student360/portfolio/portfolio.model';
import { StudentDetail } from '../../student360/student-details/student-detail.model';
import { StudentDigitalContent } from '../../student360/student-digital-content/models/student-digital-content.model';
import { CardStudentGrades } from '../../student360/student-summary/cards/student/current-grades-card/card-student-grades';
import { CheckForErrorsGuard } from '../guards/check-for-errors/check-for-errors.guard';
import { UnsavedChangesGuard } from '../guards/unsaved-changes/unsaved-changes.guard';
import { StudentAlerts } from '../models/student-alerts.model';
import { SharedService } from '../shared.service';
import { System } from '../System';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { UserService } from '../user/user.service';
import { YearService } from '../year/year.service';
import { AcademicHistorySummary } from './academic-history-summary.model';
import { AcademicHistory } from './academic-history.model';
import { Attendance, ExcessiveAttendance, SpecialAdditionalAttendance, StudentTeamAttendance } from './attendance.model';
import { CourseSchedule } from './course-schedule.model';
import { Discipline } from './discipline.model';
import { Enrollment } from './enrollment.model';
import { StudentContact } from './student-contact.model';
import { StudentDocuments } from './student-documents.model';
import { Alerts, Student } from './student.model';
import { StudentAddress } from '../../student360/student-details/view-student-address/student-address.model';
import { Year } from '../year/year.model';
import { Router } from '@angular/router';

@Injectable()
export class StudentService {

    private _previousSelectedStudent: Student;
    private _selectedStudent: Student;
    private observableSelectedStudent = new BehaviorSubject<number>(-1);
    selectedStudentStream$ = this.observableSelectedStudent.asObservable();
    public lastSelectedStudent = null;
    public students: Student[];
    private observableStudents = new BehaviorSubject<Student[]>(null);
    observableStudentsStream$ = this.observableStudents.asObservable();
    student: Student;
    private observableStudentEnrollment = new BehaviorSubject<Enrollment>(null);
    observableStudentEnrollmentStream$ = this.observableStudentEnrollment.asObservable();
    private _studentEnrollment: Enrollment;
    enrollmentYears: any[];
    studentPrimaryContacts: StudentContact[];
    recentStudents: Student[] = [];
    legacySelectedStudentId = 0;
    alerts: StudentAlerts[] = [];
    previousArrayForAlerts: number[] = [];
    selectedCourse: CourseSchedule;
    selectedSectionId = -1;
    selected: string;
    selectionIndex = 0;
    selectedProgramName: string;
    selectedServiceName: string;
    StandradTabByCurrentGrade = false;
    // V9 ONE-6156
    selectedCardCourse: CardStudentGrades;
    observableSelectedProgramAndService = new Subject();
    observableSelectedProgramAndServiceStream = this.observableSelectedProgramAndService.asObservable();

    // Common use in fine-billing, Meal-service, online Payment and fine -billing and Meal service card.
    // Online Payment Configuration
    private _onlinePaymentConfiguration: OnlinePaymentConfiguration = new OnlinePaymentConfiguration();
    public get onlinePaymentConfiguration(): OnlinePaymentConfiguration {
        return this._onlinePaymentConfiguration;
    }

    public set onlinePaymentConfiguration(value: OnlinePaymentConfiguration) {
        this._onlinePaymentConfiguration = value;
        this.observableOnlinePaymentConfiguration.next(value);
    }

    public observableOnlinePaymentConfiguration = new Subject<OnlinePaymentConfiguration>();
    observableOnlinePaymentConfigurationStream$ = this.observableOnlinePaymentConfiguration.asObservable();

    studentDocuments: StudentDocuments[];
    public observableStudentDocuments = new BehaviorSubject<StudentDocuments[]>(null);
    observableStudentDocumentsStream$ = this.observableStudentDocuments.asObservable();
    private _panelFilter: boolean;
    public get panelFilter(): boolean {
        return this._panelFilter;
    }

    public set panelFilter(value: boolean) {
        this._panelFilter = value;
        this.observablePanelFilter.next(value);
    }
    public observablePanelFilter = new Subject<boolean>();
    observablePanelFilterStream$ = this.observablePanelFilter.asObservable();


    private _medicalShowAlert: boolean;
    public get medicalShowAlert(): boolean {
        return this._medicalShowAlert;
    }

    public set medicalShowAlert(value: boolean) {
        this._medicalShowAlert = value;
        this.observableMedicalShowAlert.next(value);
    }

    public observableMedicalShowAlert = new Subject<boolean>();
    observableMedicalShowAlertStream$ = this.observableMedicalShowAlert.asObservable();

    constructor(
        private http: HttpClient,
        private userService: UserService,
        private yearService: YearService,
        private sharedService: SharedService,
        private userPreferencesService: UserPreferencesService,
        private unsavedChangesGuard: UnsavedChangesGuard,
        private checkForErrorsGuard: CheckForErrorsGuard,
        private router: Router
    ) {

        // If the academic year or school changes, reset the selected students
        this.yearService.observableDefaultYearStream$.subscribe(() => {
            this.lastSelectedStudent = null;
            if (this.selectedStudent) {
                this.getStudentEnrollments(
                    this.selectedStudent.Id,
                    this.yearService.defaultYear.Id
                ).pipe(take(1)).subscribe(enrollments => {
                    let hasEnrollment = false;
                    enrollments.forEach(enrollment => {
                        if (enrollment.SchoolId == this.yearService.defaultYear.SchoolID) {
                            hasEnrollment = true;
                        }
                    });
                    if (!hasEnrollment) {
                        this.selectedStudent = null;
                    }
                });
            }
        });
    }

    get previousSelectedStudent(): Student {
        return this._previousSelectedStudent;
    }

    get selectedStudent(): Student {
        return this._selectedStudent;
    }

    set selectedStudent(value: Student) {
        // PRE: a student to set the selected student to
        // POST: updates the student, but does NOT signal the observable for timing reasons
        //       Also, if the value is not null, sets that to the last selected student
        this._previousSelectedStudent = this.selectedStudent;
        this._selectedStudent = value;
        if (value) {
            this.lastSelectedStudent = value;
            this.updateRecentStudents(value);
        }
    }

    clearSelectedStudent() {
        this._selectedStudent = null;
        this.lastSelectedStudent = null;
        this.studentEnrollment = null;
        this.students = [];
    }

    getStudentIsValidForUser(studentId: number = 0): Observable<boolean> {
        // added if statement due to default value not always being used
        if (!studentId) {
            studentId = 0;
        }

        const queryString = '?yearId=' + this.yearService.defaultYear.Id +
            '&schoolId=' + this.yearService.defaultYear.SchoolID +
            '&studentId=' + studentId;
        return this.http.get<boolean>(System.apiURL.Student + 'GetStudentIsValidForUser' + queryString);
    }

    getParentAssignedStudents(userId, yearId, userType): Observable<Student[]> {
        return this.http.get<Student[]>(System.apiURL.Student + 'GetParentAssignedStudents?loginid=' + userId + '&yearId=' + yearId + '&userType=' + userType);
    }

    public get studentEnrollment(): Enrollment {
        return this._studentEnrollment;
    }

    public set studentEnrollment(enrollment: Enrollment) {
        if (!_.isEqual(enrollment, this._studentEnrollment)) {
            this._studentEnrollment = enrollment;
            this.observableStudentEnrollment.next(enrollment);
        }
    }

    updateStudents(students: Student[], index = 0) {
        this.students = students;
        this.observableStudents.next(students);
        if (students.length > 0) {
            this.updateSelectedStudent(this.students[index].Id);
        }
    }

    updateRecentStudents(student: Student) {
        // Remove student if already exists
        this.recentStudents = this.recentStudents.filter(s => s.Id !== student.Id);
        // Add the student to the front of the list
        this.recentStudents.unshift(student);
        // Keep the list to 10 students
        if (this.recentStudents.length > 10) {
            this.recentStudents.length = 10;
        }
    }

    clearRecentStudents() {
        this.recentStudents.length = 0;
    }

    updateSelectedStudent(studentId: number) {
        const canContinue = this.unsavedChangesGuard.checkForDeactivation() && this.checkForErrorsGuard.checkForDeactivation();
        if (canContinue instanceof Observable) {
            canContinue.subscribe(clickedOk => {
                if (clickedOk) {
                    if (studentId != null) {
                        this.updateStudentEnrollment(studentId);
                    } else {
                        this.observableSelectedStudent.next(null);
                    }
                }
            });
        } else if (canContinue) {
            if (studentId != null) {
                this.updateStudentEnrollment(studentId);
            } else {
                this.selectedStudent = null;
                this.observableSelectedStudent.next(null);
            }
        }
    }

    updateStudentEnrollment(studentId: number) {
        if (this.selectedStudent == null || this.selectedStudent.Id != studentId) {
            this.selectedStudent = this.students.filter(student => student.Id == studentId)[0];
            this.yearService.getUserYears(this.userService.userId, this.userService.userType, studentId).subscribe(years => {

                // ONE-12175
                // This should be a temporary fix only. The header should not restrict years that a student is enrolled in outside of Student 360.
                // It may be a problem in other areas outside of Reg/Enroll, but this is likely the safest quick fix for now.
                let url = this.router.url;
                if (url.indexOf('registrationEnroll') == -1) {
                    this.yearService.loadYears(years);
                    this.yearService.years = years;
                }

                if (this.yearService.student360Year != undefined) {
                    this.getStudentEnrollment(this.selectedStudent.Id, this.yearService.student360Year.Id).subscribe(enrollment => {
                        this.studentEnrollment = enrollment;
                        if (this.userService.studentInformationView) {
                            this.enrollmentYears = enrollment.EnrollmentYears;
                        }
                        this.observableSelectedStudent.next(studentId);
                    });
                } else {
                    this.getStudentEnrollment(this.selectedStudent.Id, this.yearService.defaultYear.Id).subscribe(enrollment => {
                        this.studentEnrollment = enrollment;
                        this.observableSelectedStudent.next(studentId);
                    });
                }
            });
        } else {
            this.getStudentEnrollment(this.selectedStudent.Id, this.yearService.defaultYear.Id).subscribe(enrollment => {
                this.studentEnrollment = enrollment;
                if (this.userService.studentInformationView) {
                    this.enrollmentYears = enrollment.EnrollmentYears;
                }
                this.observableSelectedStudent.next(studentId);
            });
        }
    }

    updateStudentsNFR(students: FamilyRegistrationStudent[]) {
        this.students = [];
        students.forEach(std => {
            const student = new Student();
            student.Id = std.id;
            student.FirstName = std.FirstNameNew;
            student.MiddleName = std.MiddleNameNew;
            student.LastName = std.LastNameNew;
            this.students.push(student);
        });
    }

    getStudentEnrollment(studentId: number, academicYearId: number): Observable<Enrollment> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&academicYearId=' + academicYearId;
        return this.http.get<Enrollment>(System.apiURL.Student + 'GetStudentEnrollment' + queryString);
    }

    getStudentEnrollmentYears(studentId: number): Observable<Year[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId;
        return this.http.get<Year[]>(System.apiURL.Student + 'GetStudentEnrollmentYears' + queryString);
    }

    getStudentEnrollments(studentId: number, academicYearId: number): Observable<Enrollment[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&academicYearId=' + academicYearId;
        return this.http.get<Enrollment[]>(System.apiURL.Student + 'GetStudentEnrollments' + queryString);
    }

    getStudentAttendance(studentId: number, yearId: number, showAllAttendanceCodes: boolean): Observable<Attendance[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&showAllAttendanceCodes=' + showAllAttendanceCodes;
        return this.http.get<Attendance[]>(System.apiURL.Student + 'GetStudentAttendance' + queryString);
    }

    getStudentDailyAttendance(yearId: number, studentId: number): Observable<StudentDailyAttendance[]> {
        return this.http.get<StudentDailyAttendance[]>(System.apiURL.Student + 'GetStudentDailyAttendance?yearId=' + yearId + '&studentId=' + studentId);
    }


    getAcademicHistory(studentId: number, yearId: number, isShowHSTranscript: boolean): Observable<AcademicHistory[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&isShowHSTranscript=' + isShowHSTranscript;
        return this.http.get<AcademicHistory[]>(System.apiURL.Student + 'GetAcademicHistory' + queryString);
    }

    getAcademicHistorySummary(studentId: number, yearId: number): Observable<AcademicHistorySummary> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId;
        return this.http.get<AcademicHistorySummary>(System.apiURL.Student + 'GetAcademicHistorySummary' + queryString);
    }

    getStudentIdsByStudentNumbers(studentNumbers: string[]): Observable<any> {
        return this.http.post(System.apiURL.Student + `getStudentIdsByStudentNumbers`, studentNumbers, { observe: 'response' });
    }

    getStudentSchedule(studentId: number, yearId: number, viewOption: number, includeAttendance: boolean, includeReportCardGrades: boolean, IsProgressGrades: boolean, userid: number, includeDropped: boolean, includeWithdrawnStudent: boolean, blnIncludeTerms: boolean, showAllAttendanceCodes: boolean, termDefinationId = 0): Observable<CourseSchedule[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&viewOption=' + viewOption +
            '&includeAttendance=' + includeAttendance +
            '&includeReportCardGrades=' + includeReportCardGrades +
            '&IsProgressGrades=' + IsProgressGrades +
            '&userid=' + userid +
            '&includeDropped=' + includeDropped +
            '&includeWithdrawnStudent=' + includeWithdrawnStudent +
            '&blnIncludeTerms=' + blnIncludeTerms +
            '&showAllAttendanceCodes=' + showAllAttendanceCodes +
            '&termDefinationId=' + termDefinationId;
        return this.http.get<CourseSchedule[]>(System.apiURL.Student + 'GetStudentSchedule' + queryString);
    }

    getStudent(studentId: number): Observable<Student> {
        let queryString = '';
        queryString += '?id=' + studentId;
        return this.http.get<Student>(System.apiURL.Student + 'GetStudent' + queryString);
    }

    /**
     * Retrieves an array of StudentContact for a given student
     * @param studentId The Id of the student whose contacts you want to retrieve
     */
    getStudentPrimaryContacts(studentId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId;
        return this.http.get<StudentContact[]>(System.apiURL.Student + 'GetStudentPrimaryContacts' + queryString);
    }

    getSpecialAttendance(studentId: number, yearId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId;
        return this.http.get<SpecialAdditionalAttendance[]>(System.apiURL.Student + 'GetSpecialAttendance' + queryString);
    }

    getExcessiveAttendance(studentId: number, yearId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId;
        return this.http.get<ExcessiveAttendance[]>(System.apiURL.Student + 'GetExcessiveAttendance' + queryString);
    }

    getAttendanceDaysAndHoursInfo(studentId: number, yearId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId;
        return this.http.get<any[]>(System.apiURL.Student + 'GetAttendanceDaysAndHoursInfo' + queryString);
    }

    getStudentDiscipline(studentId: number, yearId: number, userId: number) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&userId=' + userId;
        return this.http.get<Discipline[]>(System.apiURL.Student + 'GetStudentDiscipline' + queryString);
    }

    formatStudentName(firstName: string, middleName: string, lastName: string, nickName: string): string {
        let formattedName = '';
        if (this.userPreferencesService.generalPreferences) {
            const nameFormat = this.userPreferencesService.generalPreferences.StudentNameFormat;
            const middleNameFormat = this.userPreferencesService.generalPreferences.StudentMiddleNameFormat;
            const showNickname = this.userPreferencesService.generalPreferences.ShowNickname;
            if (nameFormat != '') {
                if (nameFormat == 'LNF') {
                    formattedName = lastName + ', ';
                }
                if (nameFormat != 'FILN') {
                    formattedName += firstName;
                }
                if (nameFormat == 'FILN') {
                    formattedName += firstName.substr(0, 1);
                }
                if (showNickname && nickName != null && nickName != '') {
                    formattedName += ' "' + nickName + '"';
                }
                if (middleNameFormat == 'SEN' && middleName != null && middleName != '') {
                    formattedName += ' ' + middleName;
                }
                if (middleNameFormat == 'SFL' && middleName != null && middleName != '') {
                    formattedName += ' ' + middleName.substr(0, 1);
                }
                if (nameFormat != 'LNF' && nameFormat != 'FNLI') {
                    formattedName += ' ' + lastName;
                }
                if (nameFormat == 'FNLI') {
                    formattedName += ' ' + lastName.substr(0, 1);
                }
            }
        }
        return formattedName;
    }

    getStudentDetails(studentId: number, yearId: number, userId: number, skipEnrollmentsContactsSiblings: boolean = false) {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&userId=' + userId +
            '&skipEnrollmentsContactsSiblings=' + skipEnrollmentsContactsSiblings;
        return this.http.get<StudentDetail>(System.apiURL.Student + 'GetStudentDetails' + queryString);
    }

    getStudentAddresses(studentId: number) {
        return this.http.get<StudentAddress[]>(System.apiURL.Student + 'GetStudentAddresses?studentId=' + studentId);
    }

    getStudentDocuments(studentId: number, yearId: number, showEntireFamilyDocuments: boolean = false, recentEvents: boolean = false) {
        const queryString = '?studentId=' + studentId +
            '&yearId=' + yearId +
            '&ShowDocumentsEntireFamily=' + showEntireFamilyDocuments +
            '&recentEvents=' + recentEvents;
        return this.http.get<StudentDocuments[]>(System.apiURL.Student + 'GetStudentDocuments' + queryString).pipe(
            map(results => {
                for (let i = 0; i < results.length; i++) {
                    results[i] = System.convert<StudentDocuments>(results[i], new StudentDocuments());
                }
                return results;
            })
        );
    }

    deleteStudentDocuments(studentId: number, documentId: number, familyId: number): Observable<string> {
        const queryString = '?studentId=' + studentId +
            '&documentId=' + documentId +
            '&familyId=' + familyId;
        return this.http.get<string>(System.apiURL.Student + 'DeleteStudentAttachment' + queryString);
    }
    public saveAttachment(attachment: StudentDocuments, attachmentType: string): Observable<number> {
        return this.http.post<number>(System.apiURL.Student + `SaveStudentDocument?attachmentType=${attachmentType}`, attachment);
    }
    getAlerts(studentIds: number[]): Observable<Alerts[]> {
        // POST: obtains a list of student alerts from the server

        // If it is the previous call, just use our current alerts
        if (studentIds.find(studentId => this.previousArrayForAlerts.includes(studentId))) {
            studentIds.forEach(s => {
                return this.alerts.filter(x => x.StudentId == s);  // ONE-5457
            });
        }

        // If this is not the previous call, get it from the server
        this.previousArrayForAlerts = studentIds;
        const currentDate = new Date();
        const dateString = currentDate.getMonth() + 1 + '/' + currentDate.getDate() + '/' + currentDate.getFullYear();
        const queryString = '?yearId=' + this.yearService.defaultYear.Id +
            '&schoolId=' + this.yearService.defaultYear.SchoolID +
            '&currentDate=' + dateString;
        return this.http.post<Alerts[]>(System.apiURL.Student + 'GetStudentAlerts' + queryString, studentIds).pipe(
            map(result => {
                this.alerts = result;
                return this.alerts;
            })
        );
    }

    updateSelectedProgramAndService() {
        this.observableSelectedProgramAndService.next();
    }

    // Common use in fine-billing, Meal-service, online Payment and fine -billing and Meal service card.
    getOnlinePaymentConfiguration(): Observable<OnlinePaymentConfiguration> {
        return this.sharedService.getOnlinePaymentConfiguration();
    }

    getStudentsByIds(studentIds: number[], showStudentPhoto = true): Observable<Student[]> {
        let queryString = '';
        queryString += '?showStudentPhoto=' + showStudentPhoto;
        return this.http.post<Student[]>(System.apiURL.Student + 'GetStudents' + queryString, studentIds);
    }

    getStudentsInformation(studentIds: number[]): Observable<Student[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearService.defaultYear.Id;
        return this.http.post<Student[]>(System.apiURL.Student + 'GetStudentsInformation' + queryString, studentIds);
    }

    getStudentsBasicInformation(studentIds: number[]): Observable<Student[]> {
        let queryString = '';
        queryString += '?yearId=' + this.yearService.defaultYear.Id;
        return this.http.post<Student[]>(System.apiURL.Student + 'GetStudentsBasicInformation' + queryString, studentIds);
    }

    public isStudentSpecialEd(studentId: number, forDate: Date): Observable<boolean> {
        let queryString = '';
        queryString += '?studentId=' + studentId;
        queryString += '&forDate=' + forDate;

        return this.http.get<boolean>(System.apiURL.Student + 'IsStudentSpecialEd' + queryString);
    }

    saveStudentDigitalContent(sdc: StudentDigitalContent, schoolId: number): Observable<number> {
        let paramString = '';
        paramString += '?schoolId=' + schoolId;

        return this.http.post<number>(System.apiURL.Student + 'SaveStudentDigitalContent' + paramString, sdc);
    }

    saveStudentPortfolio(document: Portfolio): Observable<any> {
        return this.http.post(System.apiURL.Student + `SaveStudentPortfolio`, document, { observe: 'response' });
    }

    getStudentNamesByStudentIds(studentIds: number[]): Observable<Student[]> {
        return this.http.post<Student[]>(System.apiURL.Student + 'GetStudentNamesByStudentIds', studentIds);
    }

    getStudentPicture(studentId: number, yearId: number): Observable<Uint8Array> {
        const queryString = '?studentId=' + studentId + '&siteYearId=' + yearId;
        return this.http.get<Uint8Array>(System.apiURL.Student + 'GetStudentPicture' + queryString);
    }

    getStudentTeamAttendanceAbsentTardiesCount(studentId: number, schoolId: number, yearId: number): Observable<StudentTeamAttendance[]> {
        let queryString = '';
        queryString += '?studentId=' + studentId +
            '&schoolId=' + schoolId +
            '&yearId=' + yearId;
        return this.http.get<StudentTeamAttendance[]>(System.apiURL.Classroom + 'GetStudentTeamAttendanceAbsentTardiesCount' + queryString);
    }

    getStudentTeams(studentId: number = 0): Observable<boolean> {
        const queryString = '?studentId=' + studentId +
            '&schoolId=' + this.yearService.defaultYear.SchoolID +
            '&year=' + this.yearService.defaultYear.AYIdentifier;

        return this.http.get<boolean>(System.apiURL.Classroom + 'GetStudentTeams' + queryString);
    }

    getStudentIdByNumber(studentNumber: string): Observable<number> {
        const queryString = '?studentNumber=' + studentNumber;

        return this.http.get<number>(System.apiURL.Student + 'getStudentIdByNumber' + queryString);
    }

    getStudentsByFamilyId(schoolId: number, yearId: number, familyId: number, includeAllStudents: boolean): Observable<Student[]> {
        const queryString = '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&familyId=' + familyId +
            '&includeAllStudents=' + includeAllStudents;

        return this.http.get<Student[]>(System.apiURL.Student + 'GetStudentsByFamilyId' + queryString);
    }

    getHouseholdParentsByFamilyID(familyId: number): Observable<StudentContact[]> {
        const queryString = '?familyId=' + familyId;

        return this.http.get<StudentContact[]>(System.apiURL.Student + 'GetHouseholdParentsByFamilyID' + queryString);
    }
}
